import { useEffect, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';

const PROTOCOLS = ['SFTP', 'FTP', 'FTPS'];
const todayISO = () => new Date().toISOString().slice(0, 10);

// Maps a form field to the template column it's auto-filled from.
const TEMPLATE_FIELD = {
  host: 'host', port: 'port', username: 'default_username', password: 'default_password',
  source_dir: 'source_dir', destination_dir: 'destination_dir'
};

function makeInitial(credential, site) {
  // file_types lives on the site (sites.sftp_file_types), not on the
  // credential, so it comes through the `site` prop. Dave wants it
  // visible on the credential form so the Push SFTP to xsiteio button
  // ships a self-contained config without a detour to Edit Site.
  const fileTypes = site && site.sftp_file_types ? String(site.sftp_file_types) : '';
  if (credential) {
    return {
      label:           credential.label || '',
      username:        credential.username || '',
      password:        credential.password || '',
      host:            credential.host || '',
      port:            credential.port != null ? String(credential.port) : '',
      protocol:        credential.protocol || 'SFTP',
      source_dir:      credential.source_dir || '',
      destination_dir: credential.destination_dir || '',
      date_received:   credential.date_received ? String(credential.date_received).slice(0, 10) : '',
      file_types:      fileTypes
    };
  }
  return {
    label: '', username: '', password: '', host: '',
    port: '24', protocol: 'SFTP', source_dir: '', destination_dir: '',
    date_received: todayISO(),
    file_types: fileTypes
  };
}

export default function CredentialForm({ open, site, credential, onClose, onSaved }) {
  const isEdit = !!credential;
  const [form, setForm] = useState(() => makeInitial(credential, site));
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [creatingDir, setCreatingDir] = useState(false);
  const [dirResult, setDirResult] = useState(null);   // { tone, message } | null
  // Push SFTP to xsiteio button state (Dave 2026-06-11).
  const [pushingSftp, setPushingSftp] = useState(false);
  const [sftpPushResult, setSftpPushResult] = useState(null); // { tone, message } | null
  // Pull SFTP from xsiteio button state (Dave 2026-06-12).
  const [pullingSftp, setPullingSftp] = useState(false);
  const [sftpPullResult, setSftpPullResult] = useState(null); // { tone, message } | null

  useEffect(() => {
    if (!open) return;
    setForm(makeInitial(credential, site));
    setError(null);
    setDirResult(null);
    // Templates are the editable defaults (Settings → Credential
    // Templates); fetching them keeps this form in sync with any edits.
    axios.get('/api/credential-templates')
      .then((r) => setTemplates(r.data.templates || []))
      .catch(() => setTemplates([]));
  }, [open, credential, site]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  // Currently-matched template (by label) — drives the "(auto)" hints
  // and the type-select value.
  const matched = templates.find((t) => t.name.toLowerCase() === String(form.label || '').toLowerCase()) || null;

  function update(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  // Apply a template's preset. `default_username`, `source_dir`, and
  // `destination_dir` may carry the [site_number] placeholder, substituted
  // with the site's number (e.g. PetroCan's per-site pricebook folder).
  function selectType(name) {
    setForm((prev) => {
      if (!name) return { ...prev, label: '' }; // Custom — keep manual values
      const t = templates.find((x) => x.name === name);
      if (!t) return { ...prev, label: name };
      const sub = (v) => String(v || '').replace(/\[site_number\]/gi, site?.site_number || '');
      return {
        ...prev,
        label:           t.name,
        host:            t.host || '',
        port:            t.port != null ? String(t.port) : '',
        username:        sub(t.default_username),
        // Fill the password only when the template carries a shared one
        // (e.g. PetroCan); otherwise leave whatever the user has typed.
        password:        t.default_password ? t.default_password : prev.password,
        source_dir:      sub(t.source_dir),
        destination_dir: sub(t.destination_dir)
      };
    });
  }

  // A field is "auto" when the matched template supplies a value for it.
  function isAuto(formField) {
    if (!matched) return false;
    const v = matched[TEMPLATE_FIELD[formField]];
    return v != null && String(v).trim() !== '';
  }
  const autoTag = (f) => isAuto(f) ? <span className="form-hint">(auto)</span> : null;

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        label:           form.label || null,
        username:        form.username,
        password:        form.password,
        host:            form.host,
        port:            form.port === '' ? null : Number(form.port),
        protocol:        form.protocol,
        source_dir:      form.source_dir || null,
        destination_dir: form.destination_dir || null,
        date_received:   form.date_received || null
      };
      const res = isEdit
        ? await axios.put(`/api/credentials/${credential.id}`, payload)
        : await axios.post(`/api/sites/${site.id}/credentials`, payload);
      // file_types lives on the site, not the credential — patch the
      // site row whenever the form's value differs from what arrived.
      const originalFileTypes = site && site.sftp_file_types ? String(site.sftp_file_types) : '';
      const nextFileTypes     = form.file_types || '';
      if (site?.id && originalFileTypes !== nextFileTypes) {
        await axios.put(`/api/sites/${site.id}`, { sftp_file_types: nextFileTypes });
      }
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    if (!window.confirm(`Delete credential "${credential.label || credential.username}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await axios.delete(`/api/credentials/${credential.id}`);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  // Create the destination directory on the SFTP host via the backend
  // (recursive mkdir). Only offered for saved credentials — needs the
  // stored connection details and destination path on the row.
  async function handleCreateDir() {
    if (!isEdit) return;
    setCreatingDir(true);
    setDirResult(null);
    try {
      const res = await axios.post('/api/bulloch/sftp-mkdir', { credential_id: credential.id });
      setDirResult(res.data.existed
        ? { tone: 'info', message: `Already exists: ${res.data.path}` }
        : { tone: 'good', message: `Created: ${res.data.path}` });
    } catch (err) {
      setDirResult({ tone: 'bad', message: err.response?.data?.error || err.message });
    } finally {
      setCreatingDir(false);
    }
  }

  // The destination-directory create button is offered once a credential
  // is saved (so it has stored connection details) and a destination
  // path exists to create. Built for PetroCan onboarding but works for
  // any saved credential with a destination directory.
  const canCreateDir = isEdit && String(form.destination_dir || '').trim() !== '';

  // Push this credential's SFTP details (+ the site's sftp_source_dir /
  // sftp_destination_dir / sftp_file_types) to xsiteio. Calls the
  // already-wired POST /api/sites/:id/xsiteio/configure-sftp endpoint
  // that ships the body shape Dave captured via DevTools 2026-06-11.
  // Only offered when the credential is saved (we need credential_id)
  // AND the site is onboarded to xsiteio (we need xsiteio_site_id).
  async function handlePushSftpToXsiteio() {
    if (!isEdit) return;
    setPushingSftp(true);
    setSftpPushResult(null);
    try {
      const res = await axios.post(
        `/api/sites/${site.id}/xsiteio/configure-sftp`,
        { credential_id: credential.id }
      );
      setSftpPushResult({
        tone: 'good',
        message: `Pushed to xsiteio site ${res.data.xsiteio_site_id}`
      });
    } catch (err) {
      setSftpPushResult({
        tone: 'bad',
        message: err.response?.data?.error || err.message
      });
    } finally {
      setPushingSftp(false);
    }
  }

  // Offer the Push button only when (a) the credential row exists locally
  // AND (b) the site has xsiteio IDs stamped. The latter we can't check
  // here without an extra fetch, so we surface the button whenever the
  // credential is saved and let the server return a clear error if the
  // site isn't onboarded yet.
  const canPushSftp = isEdit && !!site?.id;

  // Pull the live SFTP config (host/port/user/pass + dirs + file types
  // + run-time) FROM xsiteio into the form fields. After a successful
  // pull, calls onSaved so the parent re-fetches and the form re-opens
  // with the freshly-pulled values (the pull writes to the DB directly,
  // so the user sees the new values without manual Save). Dave 2026-06-12.
  async function handlePullSftpFromXsiteio() {
    if (!isEdit) return;
    setPullingSftp(true);
    setSftpPullResult(null);
    try {
      const res = await axios.post(
        `/api/sites/${site.id}/xsiteio/pull-sftp`,
        { credential_id: credential.id }
      );
      const c = res.data?.changed || {};
      const parts = [];
      if (c.credential) parts.push('credential');
      if (c.site)       parts.push(Object.keys(c.site).map((k) => k.replace(/^sftp_/, '')).join(' + '));
      setSftpPullResult({
        tone: 'good',
        message: parts.length
          ? `Pulled: ${parts.join(' / ')}`
          : 'Pulled (no changes -- already in sync).'
      });
      // Refresh the parent so the form re-opens with the pulled values.
      if (onSaved) onSaved();
    } catch (err) {
      // Surface the upstream xsiteio status + body so we can debug
      // which endpoint shape it expects.
      const d = err.response?.data || {};
      const parts = [d.error || err.message];
      if (d.status) parts.push(`status ${d.status}`);
      if (d.body)   parts.push(typeof d.body === 'string' ? d.body : JSON.stringify(d.body).slice(0, 200));
      setSftpPullResult({
        tone: 'bad',
        message: parts.join(' — ')
      });
    } finally {
      setPullingSftp(false);
    }
  }
  const canPullSftp = isEdit && !!site?.id;

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !saving) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit Credential' : 'Add Credential'}>
        <header className="modal-head">
          <h3>
            {isEdit ? `Edit Credential${credential.label ? ` · ${credential.label}` : ''}` : 'Add Credential'}
            {site?.site_number && <span className="modal-sub-inline"> · Site {site.site_number}</span>}
          </h3>
          <button type="button" className="modal-close" onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </header>

        <form className="modal-body" onSubmit={handleSubmit}>
          <label className="form-field form-field-wide">
            <span>Credential Type</span>
            <select value={matched ? matched.name : ''} onChange={(e) => selectType(e.target.value)}>
              <option value="">Custom</option>
              {templates.map((t) => <option key={t.key} value={t.name}>{t.name}</option>)}
            </select>
            {matched && (matched.file_type_support || matched.sftp_run_time) && (
              <span className="form-hint">
                {matched.file_type_support && <>File types: {matched.file_type_support}</>}
                {matched.file_type_support && matched.sftp_run_time && ' · '}
                {matched.sftp_run_time && <>Run time: {matched.sftp_run_time}</>}
              </span>
            )}
          </label>

          <div className="form-grid">
            {!matched && (
              <label className="form-field">
                <span>Label</span>
                <input type="text" value={form.label} onChange={update('label')} placeholder="e.g. Back Office" />
              </label>
            )}
            <label className="form-field">
              <span>Protocol</span>
              <select value={form.protocol} onChange={update('protocol')}>
                {PROTOCOLS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
            <label className="form-field">
              <span>Host {autoTag('host')}</span>
              <input type="text" value={form.host} onChange={update('host')} className="cell-mono" autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Port {autoTag('port')}</span>
              <input type="number" value={form.port} onChange={update('port')} className="cell-mono" />
            </label>
            <label className="form-field">
              <span>Username {autoTag('username')}</span>
              <input type="text" value={form.username} onChange={update('username')} className="cell-mono" autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Password {autoTag('password')}</span>
              <input type="text" value={form.password} onChange={update('password')} className="cell-mono" autoComplete="off" />
            </label>
            <label className="form-field">
              <span>Source Directory {autoTag('source_dir')}</span>
              <input type="text" value={form.source_dir} onChange={update('source_dir')} className="cell-mono" placeholder="/receive" />
            </label>
            <label className="form-field">
              <span>Destination Directory {autoTag('destination_dir')}</span>
              <input type="text" value={form.destination_dir} onChange={update('destination_dir')} className="cell-mono" placeholder="/send/priceb" />
              {canCreateDir && (
                <>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={handleCreateDir}
                    disabled={creatingDir || saving}
                    style={{ marginTop: 'var(--space-2)', alignSelf: 'flex-start' }}
                  >
                    {creatingDir ? 'Creating…' : 'Create Remote Directory'}
                  </button>
                  {dirResult && (
                    <span
                      className="form-hint"
                      style={{
                        marginTop: 'var(--space-1)',
                        color: dirResult.tone === 'bad' ? 'var(--color-bad)'
                             : dirResult.tone === 'good' ? 'var(--color-good)'
                             : 'var(--color-text-dim)'
                      }}
                    >
                      {dirResult.message}
                    </span>
                  )}
                </>
              )}
            </label>
            <label className="form-field">
              <span>Date Received</span>
              <DateInput value={form.date_received} onChange={update('date_received')} className="cell-mono" />
            </label>
            {/* File Type Support is a site-level field (sites.sftp_file_types) -- */}
            {/* surfaced here so the Push SFTP to xsiteio button below has the */}
            {/* complete config in one view. Saving the form patches the site. */}
            <label className="form-field form-field-wide">
              <span>File Type Support</span>
              <input
                type="text"
                value={form.file_types}
                onChange={update('file_types')}
                className="cell-mono"
                placeholder="trs,trx,sft"
                autoComplete="off"
              />
              <span className="form-hint">
                Comma-separated keys (trs, trx, sft, …). Site-level — shared by all
                credentials on this site. Used to build the valid_files regex pushed
                to xsiteio.
              </span>
            </label>
          </div>

          {canPushSftp && (
            <div style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-sunken)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flexWrap: 'wrap'
            }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePullSftpFromXsiteio}
                disabled={pullingSftp || pushingSftp || saving}
              >
                {pullingSftp ? 'Pulling…' : 'Pull SFTP from xsiteio'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePushSftpToXsiteio}
                disabled={pushingSftp || pullingSftp || saving}
              >
                {pushingSftp ? 'Pushing…' : 'Push SFTP to xsiteio'}
              </button>
              <span className="form-hint" style={{ flex: 1 }}>
                Pull reads the live values from xsiteio's "Client's SFTP details"
                form and writes them back here. Push sends this credential's
                values up to xsiteio. Use Pull to backfill sites configured
                directly on xsiteio's side.
              </span>
              {sftpPullResult && (
                <span
                  className="form-hint"
                  style={{
                    width: '100%',
                    color: sftpPullResult.tone === 'bad'  ? 'var(--color-bad)'
                         : sftpPullResult.tone === 'good' ? 'var(--color-good)'
                         : 'var(--color-text-dim)'
                  }}
                >
                  {sftpPullResult.message}
                </span>
              )}
              {sftpPushResult && (
                <span
                  className="form-hint"
                  style={{
                    width: '100%',
                    color: sftpPushResult.tone === 'bad'  ? 'var(--color-bad)'
                         : sftpPushResult.tone === 'good' ? 'var(--color-good)'
                         : 'var(--color-text-dim)'
                  }}
                >
                  {sftpPushResult.message}
                </span>
              )}
            </div>
          )}

          {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}

          <div className="form-actions">
            {isEdit && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleDelete}
                disabled={saving}
                style={{ marginRight: 'auto', color: 'var(--color-bad)' }}
              >
                Delete
              </button>
            )}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Credential')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
